import { Database, IdCard, Sparkles } from "lucide-react";

// Traits — the list/overview page (../all-traits) is wired to the real
// traits mock API (../api.ts). The trait builder below stays a local-only
// demo (see ../new-trait) since the API has no create/edit/delete endpoint
// yet; TraitRow/TraitConfig are its shapes, and TRAIT_TYPES plus the
// category-specific dropdowns without a matching real endpoint (events,
// aggregations, prediction goals, refresh, no-value handling) stay hardcoded.

export type TraitCategory = "Custom" | "Behavioral" | "Predictive";

export interface TraitRow {
  name: string;
  category: TraitCategory;
  type: string;
  sample: string;
  coverage: number;
  updated: string;
  status: "Active";
}

export interface TraitConfig {
  category: TraitCategory;
  name: string;
  label: string;
  description: string;
  dataType: string;
  enumValues: string[];
  // custom
  sourceSystem: string;
  table: string;
  field: string;
  behavior: string;
  // behavioral
  event: string;
  aggregation: string;
  window: string;
  // predictive
  goal: string;
  refresh: string;
  // additional settings
  noValue: string;
  defaultValue: string;
  availableIn: { segments: boolean; journeys: boolean; campaigns: boolean };
}

export const TRAIT_TYPES: {
  category: TraitCategory;
  icon: typeof Database;
  title: string;
  blurb: string;
  bestFor: string;
  examples: string[];
  buildTitle: string;
}[] = [
  {
    category: "Custom",
    icon: Database,
    title: "Custom/ Declared Trait",
    blurb: "Collect customer attributes directly from kyc, onboarding or manual input",
    bestFor: "Static customer information provided during onboarding or updated by your team.",
    examples: ["kyc_tier", "employer_name", "preferred_branch", "occupation_category"],
    buildTitle: "Custom Trait",
  },
  {
    category: "Behavioral",
    icon: IdCard,
    title: "Behavioral/Computed Trait",
    blurb: "Calculate traits from customer activity, transaction data and engagement patterns",
    bestFor: "Dynamic attributes that change based on customer behavior and usage patterns.",
    examples: ["balance_tier", "salary_day", "preferred_channel", "preferred_channel"],
    buildTitle: "Behavioral Trait",
  },
  {
    category: "Predictive",
    icon: Sparkles,
    title: "Predictive/ AI Trait",
    blurb: "Use AI models to predict customer scores, segments and next best actions",
    bestFor: "Dynamic attributes that change based on customer behavior and usage patterns.",
    examples: ["churn_risk_score", "loan_propensity_score", "lifetime_value_tier", "next_best_product"],
    buildTitle: "Predictive Trait",
  },
];

export const DATA_TYPES = ["Enum", "String", "Number", "Boolean", "Date"];
// Source System/Table/Field for Custom traits now come from the real
// GET /traits/sources endpoint (see api.ts, wired in new-trait/step-configure.tsx).
export const BEHAVIORS = ["sync whenever source value changes", "sync daily", "sync hourly"];
export const EVENTS = ["Wallet funded", "Salary credit detected", "Message opened", "Loan application started"];
export const AGGREGATIONS = ["Count of events", "Sum of amount", "Average of amount", "Most recent value"];
export const WINDOWS = ["Last 7 days", "Last 30 days", "Last 90 days", "All time"];
export const GOALS = ["Churn risk", "Loan propensity", "Lifetime value", "Next best product"];
export const REFRESH = ["Daily", "Weekly", "Monthly"];
export const NO_VALUE = ["Leave empty", "Use a default value", "Exclude the customer"];

export const createConfig = (category: TraitCategory, from?: TraitRow): TraitConfig => {
  const base: TraitConfig = {
    category,
    name: "",
    label: "",
    description: "",
    dataType: "Enum",
    enumValues: ["Tier 1", "Tier 2", "VIP"],
    sourceSystem: "KYC and Onboarding",
    table: "customer_kyc",
    field: "kyc_tier",
    behavior: BEHAVIORS[0],
    event: EVENTS[0],
    aggregation: AGGREGATIONS[0],
    window: WINDOWS[1],
    goal: GOALS[0],
    refresh: REFRESH[0],
    noValue: NO_VALUE[0],
    defaultValue: "",
    availableIn: { segments: true, journeys: true, campaigns: true },
  };
  if (category === "Custom") {
    Object.assign(base, { name: "kyc_tier", label: "KYC Tier", description: "Customer’s KYC tier captured during onboarding" });
  } else if (category === "Behavioral") {
    Object.assign(base, {
      name: "balance_tier",
      label: "Balance Tier",
      description: "Tier based on the customer’s wallet balance activity",
      sourceSystem: "Transactions",
      table: "wallet_transactions",
      field: "balance",
    });
  } else {
    Object.assign(base, {
      name: "churn_risk_score",
      label: "Churn Risk Score",
      description: "AI-generated likelihood that the customer will churn",
      enumValues: ["Low", "Medium", "High"],
    });
  }
  if (from) {
    Object.assign(base, {
      name: from.name,
      label: from.name.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
      dataType: from.type,
      enumValues: from.type === "Enum" ? [from.sample, ...base.enumValues.filter((v) => v !== from.sample)].slice(0, 3) : base.enumValues,
    });
  }
  return base;
};
